import { useCallback, useEffect, useState } from 'react';
import { pdApi } from '../api';
import '../screens.css';
import './Financial.css';
import { HelpPanel } from '../HelpPanel';
import { MVP_FINANCIAL_SUMMARY, MVP_VERSION_COMPARISON } from '../helpContent';
import { SelectField, PROMPT } from '@platform/ui-kit';

/**
 * Financial Modeling → MVP Financial Summary, and → Version Comparison.
 *
 * Cloned from `views/value-roi/financial-summary.ejs` and `views/value-roi/version-comparison.ejs`.
 *
 * THE NUMBERS ON THIS SCREEN ARE RECOMPUTED, NOT READ. The stored `roi_percent` and
 * `payback_years` on the model row come from a DIFFERENT formula than the one this screen
 * uses — the stored one refuses to report a negative ROI, this one will. The source keeps
 * both and shows this one here, so a version whose savings do not cover its build shows a
 * negative return rather than a flat zero.
 *
 * BASE-MODEL INHERITANCE: a non-base version whose run rates are both zero borrows them
 * from the version whose name contains "base", so a Best or Worst scenario need only
 * differ in the numbers that actually differ. The screen says when that has happened —
 * otherwise the figures look entered when they were inherited.
 */

type Dict = Record<string, unknown>;

const money = (v: unknown) =>
  v === null || v === undefined
    ? '—'
    : Number(v).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

const pct = (v: unknown) =>
  v === null || v === undefined ? '—' : `${Number(v).toFixed(1)}%`;

const years = (v: unknown) =>
  v === null || v === undefined ? 'N/A' : `${Number(v).toFixed(1)} yrs`;

const CLASS_COLOURS: Record<string, string> = {
  High: '#166534',
  Medium: '#92400e',
  Low: '#b91c1c',
};

export function FinancialSummary() {
  const [projectId, setProjectId] = useState('');
  const [opportunityId, setOpportunityId] = useState('');
  const [versionId, setVersionId] = useState<number | undefined>(undefined);
  const [pick, setPick] = useState<Dict | null>(null);
  const [summary, setSummary] = useState<Dict | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // The project/opportunity pickers reuse the cost-scenarios feed, which is the only
  // endpoint that returns both lists together.
  const loadPickers = useCallback(async () => {
    try {
      const d = (await pdApi.financial.costScenarios({
        context: 'MVP',
        project: projectId || undefined,
      })) as Dict;
      setPick(d);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [projectId]);

  useEffect(() => {
    void loadPickers();
  }, [loadPickers]);

  const load = useCallback(async () => {
    if (!opportunityId) {
      setSummary(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setSummary((await pdApi.financial.summary(opportunityId, versionId)) as Dict);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [opportunityId, versionId]);

  useEffect(() => {
    void load();
  }, [load]);

  const projects = (pick?.projects as Dict[]) ?? [];
  const opportunities = (pick?.opportunities as Dict[]) ?? [];
  const versions = (summary?.versions as Dict[]) ?? [];
  const m = (summary?.financialModel as Dict) ?? {};

  return (
    <div className="pd-screen">
      <div className="pd-page-header">
        <div>
          <h1 className="pd-page-header__title">MVP Financial Summary</h1>
          <p className="pd-page-header__subtitle">
            Savings, return and payback for one scenario, recomputed from its current cost
            lines.
          </p>
        </div>
        <div className="pd-page-header__actions">
          <HelpPanel help={MVP_FINANCIAL_SUMMARY} />
        </div>
      </div>

      {error ? <div className="pd-error">{error}</div> : null}

      {/* The source's selector row — `views/value-roi/financial-summary.ejs:203-247`.
          Wider than Cost & Scenarios' (480/570 fixed, not minimums) and it wraps. */}
      <div className="fin-selectors fin-selectors--summary">
        <div className="fin-selector fin-selector--project">
          <SelectField
            id="fs-project"
            label="Project"
            width="lg"
            placeholder={PROMPT.project}
            value={projectId}
            onChange={(v) => {
              setProjectId(v);
              setOpportunityId('');
              setVersionId(undefined);
            }}
            options={projects.map((p) => ({
              value: String(p.project_id),
              id: String(p.project_id),
              description: String(p.project_description),
            }))}
          />
        </div>

        <div className="fin-selector fin-selector--opportunity">
          <SelectField
            id="fs-opportunity"
            label="Opportunity"
            width="lg"
            value={opportunityId}
            disabled={!projectId}
            onChange={(v) => {
              setOpportunityId(v);
              setVersionId(undefined);
            }}
            placeholder={
              !projectId
                ? '— Select a Project first —'
                : opportunities.length === 0
                  ? '— No Opportunities in this Project —'
                  : PROMPT.opportunity
            }
            options={opportunities.map((o) => ({
              value: String(o.opportunity_id),
              id: String(o.opportunity_id),
              description: String(o.description),
            }))}
          />
        </div>

        {versions.length ? (
          <div className="fin-selector fin-selector--version">
            <label htmlFor="fs-version">Model Version:</label>
            <select
              id="fs-version"
              value={versionId ?? ''}
              onChange={(e) => setVersionId(e.target.value ? Number(e.target.value) : undefined)}
            >
              {versions.map((v) => (
                <option key={String(v.id)} value={String(v.id)}>
                  {String(v.name)}
                </option>
              ))}
            </select>
          </div>
        ) : null}
      </div>

      {!opportunityId ? (
        <div className="pd-empty">Choose an opportunity to see its financial summary.</div>
      ) : loading ? (
        <div className="pd-loading">Loading…</div>
      ) : (
        <>
          {m.inherited_from_base ? (
            <div className="pd-notice">
              Run rates are inherited from the Base model — this scenario has none of its
              own.
            </div>
          ) : null}

          <div className="fin-kpi-grid">
            {[
              ['Build cost', money(m.build_cost)],
              ['Annual savings', money(m.calculated_annual_savings)],
              ['Recurring costs', money(m.calculated_recurring_costs)],
              ['Net benefit', money(m.calculated_net_benefit)],
              ['ROI', pct(m.calculated_roi_percent)],
              ['Payback', years(m.calculated_payback_years)],
              ['TCO 3 years', money(m.calculated_tco_3_years)],
              ['TCO 5 years', money(m.calculated_tco_5_years)],
              ['Year 1 total', money(m.calculated_year1_total)],
              ['Annual depreciation', money(m.annual_depreciation)],
            ].map(([label, value]) => (
              <div key={label} className="fin-kpi">
                <div className="fin-kpi-label">{label}</div>
                <div className="fin-kpi-value">{value}</div>
              </div>
            ))}
          </div>

          <div className="fin-verdict">
            <div>
              <div className="fin-kpi-label">Value classification</div>
              <div
                className="fin-verdict-value"
                style={{ color: CLASS_COLOURS[String(m.value_classification)] ?? '#475569' }}
              >
                {String(m.value_classification ?? '—')}
              </div>
            </div>
            <div>
              <div className="fin-kpi-label">MVP Value Strength</div>
              <div className="fin-verdict-value">
                {m.calculated_mvp_value_strength_score === null
                  ? 'Not Assessed'
                  : `${String(m.calculated_mvp_value_strength_score)} — ${String(m.calculated_mvp_value_strength_label)}`}
              </div>
              <div className="fin-explain">
                {String(m.calculated_mvp_value_strength_explanation ?? '')}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Version Comparison — two scenarios side by side.
 *
 * The difference column's polarity is INVERTED for payback: a shorter payback is better,
 * so a negative difference is the good one. That is the source's treatment and getting it
 * backwards would invert the meaning of every comparison on the screen.
 */
export function VersionComparison() {
  const [projectId, setProjectId] = useState('');
  const [opportunityId, setOpportunityId] = useState('');
  const [pick, setPick] = useState<Dict | null>(null);
  const [versions, setVersions] = useState<Dict[]>([]);
  const [a, setA] = useState<Dict | null>(null);
  const [b, setB] = useState<Dict | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        setPick(
          (await pdApi.financial.costScenarios({
            context: 'MVP',
            project: projectId || undefined,
          })) as Dict,
        );
      } catch (e) {
        setError((e as Error).message);
      }
    })();
  }, [projectId]);

  useEffect(() => {
    void (async () => {
      if (!opportunityId) {
        setVersions([]);
        setA(null);
        setB(null);
        return;
      }
      try {
        const list = (await pdApi.financial.versions(opportunityId)).versions;
        setVersions(list);
        if (list.length >= 2) {
          setA((await pdApi.financial.summary(opportunityId, Number(list[0]!.id))) as Dict);
          setB((await pdApi.financial.summary(opportunityId, Number(list[1]!.id))) as Dict);
        } else {
          setA(null);
          setB(null);
        }
      } catch (e) {
        setError((e as Error).message);
      }
    })();
  }, [opportunityId]);

  const projects = (pick?.projects as Dict[]) ?? [];
  const opportunities = (pick?.opportunities as Dict[]) ?? [];
  const ma = (a?.financialModel as Dict) ?? {};
  const mb = (b?.financialModel as Dict) ?? {};

  const pickVersion = async (side: 'a' | 'b', id: string) => {
    const s = (await pdApi.financial.summary(opportunityId, Number(id))) as Dict;
    if (side === 'a') setA(s);
    else setB(s);
  };

  const diff = (x: unknown, y: unknown, invert = false) => {
    if (x === null || x === undefined || y === null || y === undefined) return null;
    const d = Number(y) - Number(x);
    return { value: d, good: invert ? d < 0 : d > 0 };
  };

  const ROWS: { label: string; key: string; fmt: (v: unknown) => string; invert?: boolean }[] = [
    { label: 'Build cost', key: 'build_cost', fmt: money, invert: true },
    { label: 'Annual savings', key: 'calculated_annual_savings', fmt: money },
    { label: 'Recurring costs', key: 'calculated_recurring_costs', fmt: money, invert: true },
    { label: 'Net benefit', key: 'calculated_net_benefit', fmt: money },
    { label: 'ROI', key: 'calculated_roi_percent', fmt: pct },
    { label: 'Payback', key: 'calculated_payback_years', fmt: years, invert: true },
    { label: 'TCO 3 years', key: 'calculated_tco_3_years', fmt: money, invert: true },
  ];

  return (
    <div className="pd-screen">
      <div className="pd-page-header">
        <div>
          <h1 className="pd-page-header__title">MVP Version Comparison</h1>
          <p className="pd-page-header__subtitle">
            Two scenarios of the same opportunity, side by side.
          </p>
        </div>
        <div className="pd-page-header__actions">
          <HelpPanel help={MVP_VERSION_COMPARISON} />
        </div>
      </div>

      {error ? <div className="pd-error">{error}</div> : null}

      <div className="pd-filters">
        <SelectField
          label="Project"
          width="lg"
          placeholder={PROMPT.project}
          value={projectId}
          onChange={(v) => {
            setProjectId(v);
            setOpportunityId('');
          }}
          options={projects.map((p) => ({
            value: String(p.project_id),
            id: String(p.project_id),
            description: String(p.project_description),
          }))}
        />
        <SelectField
          label="Opportunity"
          width="lg"
          placeholder={PROMPT.opportunity}
          value={opportunityId}
          disabled={!projectId}
          onChange={setOpportunityId}
          options={opportunities.map((o) => ({
            value: String(o.opportunity_id),
            id: String(o.opportunity_id),
            description: String(o.description),
          }))}
        />
        {versions.length >= 2 ? (
          <>
            <select
              value={String((a?.version as Dict)?.id ?? '')}
              onChange={(e) => pickVersion('a', e.target.value)}
            >
              {versions.map((v) => (
                <option key={String(v.id)} value={String(v.id)}>
                  A: {String(v.name)}
                </option>
              ))}
            </select>
            <select
              value={String((b?.version as Dict)?.id ?? '')}
              onChange={(e) => pickVersion('b', e.target.value)}
            >
              {versions.map((v) => (
                <option key={String(v.id)} value={String(v.id)}>
                  B: {String(v.name)}
                </option>
              ))}
            </select>
          </>
        ) : null}
      </div>

      {!opportunityId ? (
        <div className="pd-empty">Choose an opportunity to compare its scenarios.</div>
      ) : versions.length < 2 ? (
        <div className="pd-empty">
          This opportunity has fewer than two model versions — nothing to compare.
        </div>
      ) : (
        <div className="pd-table-wrap">
          <table className="pd-table">
            <thead>
              <tr>
                <th style={{ width: 220 }}>Measure</th>
                <th style={{ width: 180 }}>{String((a?.version as Dict)?.name ?? 'A')}</th>
                <th style={{ width: 180 }}>{String((b?.version as Dict)?.name ?? 'B')}</th>
                <th style={{ width: 180 }}>Difference</th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map((r) => {
                const d = diff(ma[r.key], mb[r.key], r.invert);
                return (
                  <tr key={r.key}>
                    <td>{r.label}</td>
                    <td className="pd-num">{r.fmt(ma[r.key])}</td>
                    <td className="pd-num">{r.fmt(mb[r.key])}</td>
                    <td
                      className="pd-num"
                      style={{
                        color: d === null ? '#94a3b8' : d.good ? '#166534' : d.value === 0 ? '#64748b' : '#b91c1c',
                      }}
                    >
                      {d === null
                        ? 'N/A'
                        : `${d.value >= 0 ? '+' : ''}${r.fmt(d.value)}`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
